import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import _ from 'lodash';

import masterDataService from '../../service/masterData.service';
import { DeleteDialogUI, FormBase, FormHeaderUI } from '../Interface';
import { generateAutocompleteItems } from '../../utils/helper';
import AttendanceSettlementLine from './AttendanceSettlementLine';
import AttendanceSettlementFields from './AttendanceSettlementFields';
import AttendanceSettlementReports from './AttendanceSettlementReports';
import useMenus from '../../context/MenuContext';
import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';
import attendanceSettlementService from '../../service/attendanceSettlement/attendanceSettlement.service';

export const AttendanceSettlementPage = () => {
  const { menus, getMenus, getMenuSubMenuId, getCanEdit } = useMenus();
  const [initValues, setInitValues] = useState({});
  const [selectedValues, setSelectedValues] = useState({});
  const [defaultValues, setDefaultValues] = useState({});
  const [autocompleteOptions, setAutoCompleteOptions] = useState({
    project: [],
    company: [],
    user: [],
  });
  const [canEdit, setCanEdit] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [refetchTable, setRefetchTable] = useState(false);

  const def = {
    createdAt: dayjs(new Date()),
    projectId: null,
    companyId: null,
    attendanceSettlementLine: [],
  };

  useEffect(() => {
    getMenus();
    setDefaultValues(def);
    initDefaultValues();
  }, []);

  useEffect(() => {
    if (Object.keys(selectedValues).length > 0) {
      valuesToForm();
    } else {
      setInitValues(defaultValues);
    }
  }, [selectedValues]);

  useEffect(() => {
    setCanEdit(getCanEdit(getMenuSubMenuId('attendanceSettlement')));
  }, [menus]);

  const initDefaultValues = () => {
    const promiseArray = [];

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .projectToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .companyToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .resourceOneToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    Promise.all(promiseArray)
      .then((resp) => {
        const aData = {
          project: resp[0],
          company: resp[1],
          user: resp[2],
        };

        const filteredProject = aData.project.filter((p) => p.active);

        const acOptions = {
          project: generateAutocompleteItems(filteredProject, 'projectName', 'projectId'),
          company: generateAutocompleteItems(aData.company, 'name', 'id'),
          user: generateAutocompleteItems(aData.user, 'name', 'id'),
        };
        setAutoCompleteOptions(acOptions);
      })
      .finally(() => {});
  };

  const valuesToForm = () => {
    const { createdAt, projectId, companyId, attendanceSettlementLine } = selectedValues;

    setInitValues({
      createdAt: dayjs(createdAt),
      projectId,
      companyId,
      attendanceSettlementLine: attendanceSettlementLine.map((asl) => ({
        id: asl.id,
        userId: asl.userId,
        timeSpent: asl.timeSpent,
        attendanceDate: dayjs(asl.attendanceDate),
        price: asl.price,
        currencyCode: asl.currencyCode,
        itemCode: asl.itemCode,
      })),
    });
  };

  const handleRemoveElement = async () => {
    attendanceSettlementService.deleteData(selectedValues.id).then(() => {
      addNotification({
        content: 'Sikeres törlés!',
        type: NotificationType.WARNING,
      });
      setRefetchTable(true);
      setShowDeleteDialog(false);
      setSelectedValues(defaultValues);
    });
  };

  return (
    <>
      <FormBase
        name="AttendanceSettlement"
        leftPanelContent={
          <>
            <div className="h-full overflow-auto custom-scrollbar">
              <FormHeaderUI
                deleteClick={() => {
                  setShowDeleteDialog(true);
                }}
                deleteDisabled={!canEdit || !selectedValues.id}
              />
              <AttendanceSettlementFields
                viewField={true}
                selectedValues={selectedValues}
                autocompleteOptions={autocompleteOptions}
                values={initValues}
              />
              <AttendanceSettlementLine disabled={true} autocompleteOptions={autocompleteOptions} values={initValues} />
            </div>
          </>
        }
        rightPanelContent={
          <>
            <AttendanceSettlementReports
              values={{ selectedValues, setSelectedValues }}
              autocompleteOptions={autocompleteOptions}
              refetch={{ refetchTable, setRefetchTable }}
            />
          </>
        }
      />
      <DeleteDialogUI
        handleRemoveElement={handleRemoveElement}
        show={showDeleteDialog}
        onHide={() => setShowDeleteDialog(false)}
      />
    </>
  );
};
